import { Head, Link } from "@inertiajs/react"
import { AppWindow, Edit, ExternalLink, FileText, Plus } from "lucide-react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

import { DiskUsageLine } from "@/components/charts/disk-usage-line"
import { DiskUsagePie } from "@/components/charts/disk-usage-pie"
import Heading from "@/components/heading"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { getEnvironmentColor } from "@/lib/utils"
import {
  appPath,
  editServerPath,
  logrotateConfigsServerPath,
  monitConfigsServerPath,
  nginxConfigsServerPath,
  newServerAppPath,
  serverAppsPath,
  serverPath,
  serversPath,
} from "@/routes"
import type {
  App,
  BreadcrumbItem,
  DiskUsageSnapshot,
  Server,
} from "@/types"

interface ServerShowProps {
  server: Server & {
    apps: Pick<App, "id" | "name" | "app_url" | "location" | "environment">[]
  }
  mostRecentDiskSnapshot?: DiskUsageSnapshot | null
  monthlyDiskSnapshots?: DiskUsageSnapshot[]
}

export default function ServerShow({
  server,
  mostRecentDiskSnapshot,
  monthlyDiskSnapshots,
}: ServerShowProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Servers", href: serversPath() },
    { title: server.name, href: serverPath(server.id) },
  ]

  // Prepare detailed pie chart data from most recent snapshot
  const pieChartData = mostRecentDiskSnapshot
    ? (() => {
        const data = []
        const breakdown = mostRecentDiskSnapshot.breakdown || {}

        // Add apps
        if (breakdown.apps && Array.isArray(breakdown.apps)) {
          breakdown.apps.forEach((app: { name: string; size: number }) => {
            if (app.size > 0) {
              data.push({
                name: `App: ${app.name}`,
                value: app.size,
                color: "#3b82f6", // blue
              })
            }
          })
        }

        // Add watched directories
        if (
          breakdown.watched_directories &&
          Array.isArray(breakdown.watched_directories)
        ) {
          breakdown.watched_directories.forEach(
            (dir: { name: string; size: number }) => {
              if (dir.size > 0) {
                data.push({
                  name: `Dir: ${dir.name}`,
                  value: dir.size,
                  color: "#8b5cf6", // violet
                })
              }
            },
          )
        }

        // Add "other used" space
        if (breakdown.other_used && breakdown.other_used > 0) {
          data.push({
            name: "Other",
            value: breakdown.other_used,
            color: "#6b7280", // gray
          })
        }

        // Add available space
        if (mostRecentDiskSnapshot.available_bytes) {
          data.push({
            name: "Available",
            value: mostRecentDiskSnapshot.available_bytes,
            color: "#10b981", // green
          })
        }

        return data
      })()
    : []

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={server.name} />

      <div className="p-6">
        <div className="flex items-center justify-between">
          <Heading
            title={server.name}
            description={server.hostname || "Server overview"}
          />
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link href={editServerPath(server.id)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
            <Button asChild>
              <Link href={newServerAppPath(server.id)}>
                <Plus className="mr-2 h-4 w-4" />
                Add App
              </Link>
            </Button>
          </div>
        </div>

        {server.info && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Server Info</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {server.info}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Disk Usage Charts */}
        {(mostRecentDiskSnapshot ||
          (monthlyDiskSnapshots && monthlyDiskSnapshots.length > 0)) && (
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {/* Current Disk Usage - Pie Chart */}
              {mostRecentDiskSnapshot && (
                <Card>
                  <CardHeader>
                    <CardTitle>Current Disk Usage</CardTitle>
                    <CardDescription>
                      Most recent snapshot from {mostRecentDiskSnapshot.path}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DiskUsagePie
                      data={pieChartData}
                      usedBytes={mostRecentDiskSnapshot.used_bytes}
                      totalBytes={mostRecentDiskSnapshot.total_bytes}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Disk Usage Over Time - Line Chart */}
              {monthlyDiskSnapshots && monthlyDiskSnapshots.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Disk Usage Trend</CardTitle>
                    <CardDescription>Last 30 days</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DiskUsageLine data={monthlyDiskSnapshots} />
                  </CardContent>
                </Card>
              )}
            </div>
          )}

        <div className="mt-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold">Applications</h2>
            <Link
              href={serverAppsPath(server.id)}
              className="text-primary text-sm hover:underline"
            >
              View all →
            </Link>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {server.apps.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <AppWindow className="text-muted-foreground h-12 w-12" />
                  <h3 className="mt-4 text-lg font-semibold">No apps yet</h3>
                  <p className="text-muted-foreground mt-2 text-sm">
                    Add your first application to this server
                  </p>
                  <Button asChild className="mt-4">
                    <Link href={newServerAppPath(server.id)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add App
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              server.apps.map((app) => (
                <Link key={app.id} href={appPath(app.id)}>
                  <Card className="group cursor-pointer transition-shadow hover:shadow-md">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AppWindow className="h-5 w-5" />
                        {app.name}
                        {app.environment && (
                          <Badge
                            className="ml-auto"
                            style={{
                              backgroundColor: getEnvironmentColor(
                                app.environment,
                              ),
                              color: "white",
                              borderColor: getEnvironmentColor(app.environment),
                            }}
                          >
                            {app.environment}
                          </Badge>
                        )}
                      </CardTitle>
                      {app.location && (
                        <CardDescription className="truncate">
                          {app.location}
                        </CardDescription>
                      )}
                    </CardHeader>
                    {app.app_url && (
                      <CardContent>
                        <p className="text-muted-foreground flex items-center gap-1 truncate text-sm">
                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                          {app.app_url}
                        </p>
                      </CardContent>
                    )}
                  </Card>
                </Link>
              ))
            )}
          </div>
        </div>

        <div className="mt-8">
          <h2 className="mb-4 text-xl font-semibold">System Configuration</h2>
          <div className="flex flex-wrap gap-4">
          <Link href={logrotateConfigsServerPath(server.id)}>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Logrotate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground flex items-center gap-1 truncate text-sm">
                  View logrotate configuration files from /etc/logrotate.d/
                </p>
              </CardContent>
            </Card>
            </Link>


            <Link href={monitConfigsServerPath(server.id)}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Monit
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground flex items-center gap-1 truncate text-sm">
                    View Monit configuration files from /etc/monit/conf.d/
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link href={nginxConfigsServerPath(server.id)}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Nginx
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground flex items-center gap-1 truncate text-sm">
                    View available and enabled Nginx configs from
                    /etc/nginx/sites-available/ and /etc/nginx/sites-enabled/
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
